import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Plus, X, ShieldCheck, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import api from "../../lib/api";

/**
 * Card de configuración del envío automático de pedidos al proveedor.
 *
 * El owner puede:
 * - Activar/desactivar el envío automático (OFF por defecto).
 * - Establecer un importe mínimo para evitar pedidos diminutos automáticos.
 * - Definir una whitelist de proveedores "de confianza" (sólo a estos se
 *   manda automáticamente; vacío = nadie auto-envía).
 * - Recibir copia (CC) y añadir destinatarios extra.
 */
export default function AutoReorderConfigCard({ restaurantId }) {
  const [config, setConfig] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCcEmail, setNewCcEmail] = useState("");

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [cfg, sups] = await Promise.all([
        api.request(`/restaurants/${restaurantId}/auto-reorder/config`),
        api.request(`/restaurants/${restaurantId}/suppliers`).catch(() => []),
      ]);
      setConfig(cfg);
      setSuppliers(Array.isArray(sups) ? sups : []);
    } catch {
      toast.error("Error cargando configuración");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [restaurantId]);  // eslint-disable-line

  const persist = async (patch) => {
    setSaving(true);
    try {
      const updated = await api.request(`/restaurants/${restaurantId}/auto-reorder/config`, {
        method: "PUT",
        body: JSON.stringify(patch),
      });
      setConfig(updated);
      toast.success("Guardado");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleSupplierTrust = (sid) => {
    const list = config?.trusted_supplier_ids || [];
    const next = list.includes(sid) ? list.filter(s => s !== sid) : [...list, sid];
    persist({ trusted_supplier_ids: next });
  };

  const removeCc = (email) => {
    const next = (config?.extra_cc_emails || []).filter(e => e !== email);
    persist({ extra_cc_emails: next });
  };

  const addCc = () => {
    const v = (newCcEmail || "").trim();
    if (!v.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    if ((config?.extra_cc_emails || []).includes(v)) {
      setNewCcEmail("");
      return;
    }
    persist({ extra_cc_emails: [...(config?.extra_cc_emails || []), v] });
    setNewCcEmail("");
  };

  if (loading || !config) {
    return (
      <div className="border rounded-xl p-6 flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando configuración…
      </div>
    );
  }

  const enabled = config.auto_email_enabled === true;
  const trusted = config.trusted_supplier_ids || [];
  const cc = config.extra_cc_emails || [];

  return (
    <div className="border rounded-xl p-6 space-y-6" data-testid="auto-reorder-config-card">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-orange-500/10 text-orange-700 flex items-center justify-center flex-shrink-0">
            <Send className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-base font-semibold mb-1">Envío automático de pedidos al proveedor</h3>
            <p className="text-sm text-muted-foreground">
              Cuando un ingrediente cae bajo mínimos, MenuGenius genera un borrador de pedido automáticamente.
              Si activas esta opción, el borrador se envía al proveedor por email sin que tengas que abrir la app.
            </p>
          </div>
        </div>
        <Switch
          checked={enabled}
          disabled={saving}
          onCheckedChange={(v) => persist({ auto_email_enabled: v })}
          data-testid="auto-reorder-enabled-toggle"
        />
      </header>

      {/* Min amount */}
      <div className={!enabled ? "opacity-50 pointer-events-none" : ""}>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-2 block">
          Importe mínimo para auto-enviar
        </Label>
        <div className="flex items-center gap-2 max-w-xs">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={config.auto_email_min_amount}
            onChange={(e) => setConfig({ ...config, auto_email_min_amount: parseFloat(e.target.value) || 0 })}
            onBlur={(e) => persist({ auto_email_min_amount: parseFloat(e.target.value) || 0 })}
            data-testid="auto-reorder-min-amount-input"
            className="text-right"
          />
          <span className="text-sm text-muted-foreground">€</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">
          Pedidos por debajo de este importe se quedan en borrador esperando tu revisión manual.
        </p>
      </div>

      {/* CC owner */}
      <div className={`flex items-center justify-between ${!enabled ? "opacity-50 pointer-events-none" : ""}`}>
        <div className="flex-1 min-w-0">
          <Label className="text-sm font-medium">Mandarme copia (CC)</Label>
          <p className="text-xs text-muted-foreground">Recibirás una copia de cada email enviado al proveedor.</p>
        </div>
        <Switch
          checked={config.cc_owner !== false}
          disabled={saving}
          onCheckedChange={(v) => persist({ cc_owner: v })}
          data-testid="auto-reorder-cc-owner-toggle"
        />
      </div>

      {/* Extra CC list */}
      {enabled && (
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-2 block">
            Otros destinatarios en CC
          </Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {cc.length === 0 && (
              <span className="text-xs text-muted-foreground italic">Sin destinatarios extra</span>
            )}
            {cc.map(email => (
              <span
                key={email}
                className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded-full"
                data-testid={`cc-email-pill-${email}`}
              >
                <Mail className="w-3 h-3" /> {email}
                <button
                  onClick={() => removeCc(email)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={`Eliminar ${email}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 max-w-md">
            <Input
              type="email"
              placeholder="email@dominio.com"
              value={newCcEmail}
              onChange={(e) => setNewCcEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCc()}
              data-testid="auto-reorder-cc-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCc}
              data-testid="auto-reorder-cc-add-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Suppliers whitelist */}
      {enabled && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" /> Proveedores de confianza
              </Label>
              <p className="text-xs text-muted-foreground">
                Sólo se enviará automáticamente a los proveedores marcados aquí. Los demás se quedan en borrador.
              </p>
            </div>
            <span className="text-xs text-muted-foreground" data-testid="auto-reorder-trusted-count">
              {trusted.length} / {suppliers.length}
            </span>
          </div>
          {suppliers.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-2">
              No tienes proveedores creados. Añade uno desde Inventario → Proveedores.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-auto pr-1">
              {suppliers.map(sup => {
                const isTrusted = trusted.includes(sup.supplier_id);
                const hasEmail = !!sup.email;
                return (
                  <button
                    key={sup.supplier_id}
                    type="button"
                    disabled={!hasEmail}
                    onClick={() => toggleSupplierTrust(sup.supplier_id)}
                    data-testid={`supplier-trust-${sup.supplier_id}`}
                    className={`text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-all ${
                      isTrusted
                        ? "bg-emerald-50 border-emerald-300"
                        : "bg-background border-border hover:bg-muted"
                    } ${!hasEmail ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{sup.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {sup.email || "Sin email — añádelo en su ficha"}
                      </p>
                    </div>
                    {isTrusted && <ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-muted-foreground border-t pt-4">
        <strong>¿Cuándo se envía?</strong> El cron de par-level corre cada día a las 07:00 UTC y, si hay
        ingredientes bajo mínimos, agrupa por proveedor y envía si: <em>auto-envío activado</em>,
        <em> proveedor de confianza</em>, <em>importe ≥ mínimo</em>, e <em>email del proveedor configurado</em>.
        Si falta alguno, el pedido se queda en borrador para que lo revises.
      </div>

      <SentOrdersHistoryPanel restaurantId={restaurantId} />
    </div>
  );
}

// ─── Histórico de pedidos enviados ──────────────────────────────────────
function SentOrdersHistoryPanel({ restaurantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    api.request(`/restaurants/${restaurantId}/orders/sent-history?days=30`)
      .then(r => setData(r))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [restaurantId]);

  if (loading) {
    return (
      <div className="border-t pt-4 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando histórico…
      </div>
    );
  }
  if (!data || data.counts.total === 0) {
    return (
      <div className="border-t pt-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Histórico (últimos 30 días)</p>
        <p className="text-xs text-muted-foreground italic">
          Aún no se ha enviado ningún pedido al proveedor desde aquí. Cuando empieces a usar el envío manual o
          automático, aparecerán aquí los últimos 30 días.
        </p>
      </div>
    );
  }

  const { counts, items } = data;
  const visible = showAll ? items : items.slice(0, 5);
  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso?.slice(0, 16) || ""; }
  };

  return (
    <div className="border-t pt-5 space-y-3" data-testid="sent-orders-history-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Histórico de envíos · últimos 30 días</p>
        <span className="text-xs text-muted-foreground">{counts.total_amount.toFixed(2)}€ enviados</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border bg-emerald-50 dark:bg-emerald-950/20 p-3 text-center" data-testid="sent-counts-auto">
          <p className="text-2xl font-bold text-emerald-700">{counts.auto}</p>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">Auto</p>
        </div>
        <div className="rounded-lg border bg-blue-50 dark:bg-blue-950/20 p-3 text-center" data-testid="sent-counts-manual">
          <p className="text-2xl font-bold text-blue-700">{counts.manual}</p>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">Manual</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-3 text-center" data-testid="sent-counts-total">
          <p className="text-2xl font-bold">{counts.total}</p>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">Total</p>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <ul className="divide-y" data-testid="sent-orders-list">
          {visible.map(o => (
            <li key={o.order_id} className="px-3 py-2 flex items-center gap-3 text-xs" data-testid={`sent-order-${o.order_id}`}>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                  o.sent_via === "auto_reorder"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {o.sent_via === "auto_reorder" ? "AUTO" : "MANUAL"}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{o.supplier_name || o.sent_to_email || "—"}</p>
                <p className="text-muted-foreground truncate">
                  {o.sent_to_email} · {o.items_count} ítem{o.items_count === 1 ? "" : "s"}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-medium">{(o.total_amount || 0).toFixed(2)}€</p>
                <p className="text-muted-foreground">{fmtDate(o.sent_at)}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {items.length > 5 && (
        <button
          onClick={() => setShowAll(s => !s)}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="sent-orders-toggle-all"
        >
          {showAll ? "Ver menos" : `Ver los ${items.length} envíos completos`}
        </button>
      )}
    </div>
  );
}
